import * as THREE from 'three';
import { DEBUG_ENABLED } from './DebugFlags';
import { debugDock } from './DebugDock';

/**
 * Live, debug-only visualization of the buoyancy inputs and the force vectors
 * that drive the ship.
 *
 * The hub (`Game`) computes every number — the four probe world positions, the
 * sampled water height under each one, the target/actual heave-pitch-roll and
 * the wind/thrust/rudder/velocity vectors — and hands them to `update()` as a
 * `SeaVizFrame`. This class only *draws*; it never samples the wave field, so
 * the picture can never disagree with the published diagnostics.
 *
 * Zero cost when hidden: `setVisible(false)` flips `group.visible` and skips
 * `update()` entirely, so no draw calls are issued and the readout does no
 * per-frame work. The whole overlay is off until the hub enables it.
 */

export interface SeaVizProbe {
  /** Probe position in world space (the point on the hull that is sampled). */
  x: number;
  y: number;
  z: number;
  /** Sampled water surface height at (x, z), already computed by the hub. */
  waterY: number;
}

export interface SeaVizFrame {
  /** Exactly 4, in the BuoyancySystem probe order: bow, stern, port, starboard. */
  probes: SeaVizProbe[];
  target: { heave: number; pitch: number; roll: number };
  actual: { heave: number; pitch: number; roll: number };
  vectors: {
    /** Wind direction in radians; world vector is (cos(dir), 0, -sin(dir)). */
    wind: { dir: number; strength: number };
    /** Signed thrust along the ship's forward axis (positive = forward). */
    thrust: number;
    /** Signed rudder along the ship's port axis (positive = port), [-1, 1]. */
    rudder: number;
    /** Ship velocity in world metres/second. */
    velocity: { x: number; z: number };
  };
}

/** Probe labels, matching the frozen probe order (bow, stern, port, starboard). */
const PROBE_NAMES = ['bow', 'stern', 'port', 'starboard'] as const;

/** ~10 Hz cap on readout DOM writes. */
const UI_UPDATE_MS = 100;

const RENDER_ORDER = 999;

const RING_INNER = 0.4;
const RING_OUTER = 0.62;
const RING_SEGMENTS = 28;

/** Submerged probe (waterY > y) is warm; emerged probe is cool. */
const COLOR_SUBMERGED = 0xff6a3d;
const COLOR_EMERGED = 0x4dd9ff;

const ARROW_WIND_COLOR = 0x9ae6ff;
const ARROW_THRUST_COLOR = 0x5dff8a;
const ARROW_RUDDER_COLOR = 0xff5bd1;
const ARROW_VELOCITY_COLOR = 0xffd166;

/**
 * Arrow length scales. These are visual constants, not physics: the hub passes
 * the real numbers, and we scale them into metres so each arrow stays readable
 * at chase-camera distance without dominating the scene.
 */
const WIND_SCALE = 4; // metres per unit wind strength
const THRUST_SCALE = 1.5; // metres per unit thrust
const RUDDER_SCALE = 3; // metres per unit rudder deflection
const VELOCITY_SCALE = 0.5; // metres per m/s of ship speed

const MIN_ARROW_LENGTH = 0.05;
const MAX_ARROW_LENGTH = 14;
const ARROW_HEAD_RATIO = 0.25;
const ARROW_HEAD_WIDTH_RATIO = 0.5;

/** Lift the arrow bundle above the deck so horizontal vectors stay readable. */
const ARROW_Y_OFFSET = 0.75;

export class SeaViz {
  private readonly scene: THREE.Scene;
  private readonly group = new THREE.Group();

  private readonly ringGeometry: THREE.RingGeometry;
  private readonly stemGeometry: THREE.BufferGeometry;
  private readonly ringMeshes: THREE.Mesh[] = [];
  private readonly ringMaterials: THREE.MeshBasicMaterial[] = [];
  private readonly stemLines: THREE.Line[] = [];
  private readonly stemMaterials: THREE.LineBasicMaterial[] = [];

  private readonly windArrow: THREE.ArrowHelper;
  private readonly thrustArrow: THREE.ArrowHelper;
  private readonly rudderArrow: THREE.ArrowHelper;
  private readonly velocityArrow: THREE.ArrowHelper;

  private readonly root: HTMLElement;
  private readonly body: HTMLElement;

  private visible = false;
  private lastUiUpdate = 0;
  private renderedText = '';

  private readonly centroid = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly lateral = new THREE.Vector3();
  private readonly dir = new THREE.Vector3();
  private readonly flipDir = new THREE.Vector3();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.group.name = 'SeaViz';
    this.group.visible = false;
    scene.add(this.group);

    // One shared ring + one shared vertical stem geometry for all four probes;
    // per-probe colour/length is applied through material + transform.
    this.ringGeometry = new THREE.RingGeometry(RING_INNER, RING_OUTER, RING_SEGMENTS);
    this.stemGeometry = new THREE.BufferGeometry();
    this.stemGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute([0, 0, 0, 0, 1, 0], 3),
    );

    for (let i = 0; i < 4; i += 1) {
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: COLOR_EMERGED,
        toneMapped: false,
        side: THREE.DoubleSide,
        depthTest: false,
      });
      const ring = new THREE.Mesh(this.ringGeometry, ringMaterial);
      ring.rotation.x = -Math.PI / 2; // lay the XY ring flat on the water
      ring.renderOrder = RENDER_ORDER;
      this.ringMaterials.push(ringMaterial);
      this.ringMeshes.push(ring);
      this.group.add(ring);

      const stemMaterial = new THREE.LineBasicMaterial({
        color: COLOR_EMERGED,
        toneMapped: false,
        depthTest: false,
      });
      const stem = new THREE.Line(this.stemGeometry, stemMaterial);
      stem.renderOrder = RENDER_ORDER;
      this.stemMaterials.push(stemMaterial);
      this.stemLines.push(stem);
      this.group.add(stem);
    }

    this.windArrow = this.createArrow(ARROW_WIND_COLOR);
    this.thrustArrow = this.createArrow(ARROW_THRUST_COLOR);
    this.rudderArrow = this.createArrow(ARROW_RUDDER_COLOR);
    this.velocityArrow = this.createArrow(ARROW_VELOCITY_COLOR);
    this.group.add(this.windArrow, this.thrustArrow, this.rudderArrow, this.velocityArrow);

    const readout = this.createReadout();
    this.root = readout.root;
    this.body = readout.body;

    // Participate in the shared DEBUG dock only when debug tooling is on, so a
    // production page never gains the dock button. When registered, the dock
    // (not `setVisible`) controls show/hide via `body.debug-open`.
    if (DEBUG_ENABLED) debugDock.register(this.root);
    this.root.hidden = true;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.group.visible = visible;
    this.root.hidden = !visible;
    if (!visible) {
      this.renderedText = '';
      this.body.textContent = '';
    }
  }

  update(frame: SeaVizFrame): void {
    if (!this.visible) return;
    const probes = frame.probes;
    if (probes.length !== 4) return;

    // Probe dummies: a flat ring on the sampled water surface plus a vertical
    // stem connecting the hull probe point to that surface.
    let cx = 0;
    let cy = 0;
    let cz = 0;
    for (let i = 0; i < 4; i += 1) {
      const probe = probes[i];
      const ring = this.ringMeshes[i];
      ring.position.set(probe.x, probe.waterY, probe.z);

      const stem = this.stemLines[i];
      stem.position.set(probe.x, probe.y, probe.z);
      stem.scale.set(1, probe.waterY - probe.y, 1);

      const submerged = probe.waterY - probe.y > 0;
      const color = submerged ? COLOR_SUBMERGED : COLOR_EMERGED;
      this.ringMaterials[i].color.setHex(color);
      this.stemMaterials[i].color.setHex(color);

      cx += probe.x;
      cy += probe.y;
      cz += probe.z;
    }
    this.centroid.set(cx / 4, cy / 4 + ARROW_Y_OFFSET, cz / 4);

    // The probe layout encodes the ship axes: bow→stern is forward, and
    // port→starboard is lateral (positive = port, matching `+Z = port`).
    const bow = probes[0];
    const stern = probes[1];
    const port = probes[2];
    const starboard = probes[3];
    this.forward.set(bow.x - stern.x, 0, bow.z - stern.z);
    if (this.forward.lengthSq() < 1e-9) this.forward.set(1, 0, 0);
    this.forward.normalize();
    this.lateral.set(port.x - starboard.x, 0, port.z - starboard.z);
    if (this.lateral.lengthSq() < 1e-9) this.lateral.set(0, 0, 1);
    this.lateral.normalize();

    const wind = frame.vectors.wind;
    const velocity = frame.vectors.velocity;
    const velocitySpeed = Math.hypot(velocity.x, velocity.z);

    this.dir.set(Math.cos(wind.dir), 0, -Math.sin(wind.dir));
    this.updateArrow(this.windArrow, this.dir, wind.strength * WIND_SCALE);

    this.updateArrow(this.thrustArrow, this.forward, frame.vectors.thrust * THRUST_SCALE);
    this.updateArrow(this.rudderArrow, this.lateral, frame.vectors.rudder * RUDDER_SCALE);

    this.dir.set(velocity.x, 0, velocity.z);
    this.updateArrow(this.velocityArrow, this.dir, velocitySpeed * VELOCITY_SCALE);

    this.updateReadout(frame, velocitySpeed, wind.strength);
  }

  dispose(): void {
    this.scene.remove(this.group);

    for (const material of this.ringMaterials) material.dispose();
    for (const material of this.stemMaterials) material.dispose();
    this.ringGeometry.dispose();
    this.stemGeometry.dispose();

    // ArrowHelper shares its line/cone *geometries* across all instances, so
    // only the per-instance materials are safe to dispose here.
    for (const arrow of [
      this.windArrow,
      this.thrustArrow,
      this.rudderArrow,
      this.velocityArrow,
    ]) {
      (arrow.line.material as THREE.LineBasicMaterial).dispose();
      (arrow.cone.material as THREE.MeshBasicMaterial).dispose();
    }

    if (this.root.parentElement) this.root.remove();
    this.renderedText = '';
  }

  private createArrow(color: number): THREE.ArrowHelper {
    const arrow = new THREE.ArrowHelper(
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(),
      1,
      color,
      0.25,
      0.12,
    );
    const lineMaterial = arrow.line.material as THREE.LineBasicMaterial;
    const coneMaterial = arrow.cone.material as THREE.MeshBasicMaterial;
    lineMaterial.toneMapped = false;
    lineMaterial.depthTest = false;
    coneMaterial.toneMapped = false;
    coneMaterial.depthTest = false;
    arrow.line.renderOrder = RENDER_ORDER;
    arrow.cone.renderOrder = RENDER_ORDER;
    return arrow;
  }

  private updateArrow(
    arrow: THREE.ArrowHelper,
    direction: THREE.Vector3,
    signedLength: number,
  ): void {
    if (signedLength < 0) {
      this.flipDir.copy(direction).multiplyScalar(-1);
    } else {
      this.flipDir.copy(direction);
    }
    if (this.flipDir.lengthSq() < 1e-9) this.flipDir.set(1, 0, 0);
    this.flipDir.normalize();

    const length = THREE.MathUtils.clamp(
      Math.abs(signedLength),
      MIN_ARROW_LENGTH,
      MAX_ARROW_LENGTH,
    );
    const headLength = length * ARROW_HEAD_RATIO;

    arrow.position.copy(this.centroid);
    arrow.setDirection(this.flipDir);
    arrow.setLength(length, headLength, headLength * ARROW_HEAD_WIDTH_RATIO);
  }

  private updateReadout(frame: SeaVizFrame, velocitySpeed: number, windStrength: number): void {
    const now = performance.now();
    if (now - this.lastUiUpdate < UI_UPDATE_MS) return;
    this.lastUiUpdate = now;

    const text = this.formatReadout(frame, velocitySpeed, windStrength);
    if (text === this.renderedText) return;
    this.renderedText = text;
    this.body.textContent = text;
  }

  private formatReadout(
    frame: SeaVizFrame,
    velocitySpeed: number,
    windStrength: number,
  ): string {
    const n = (value: number): string => (Number.isFinite(value) ? value.toFixed(2) : '—');
    const probes = frame.probes;
    const meanWater = (probes[0].waterY + probes[1].waterY + probes[2].waterY + probes[3].waterY) / 4;

    const lines: string[] = ['PROBES (water Y)'];
    for (let i = 0; i < 4; i += 1) {
      lines.push(`  ${PROBE_NAMES[i].padEnd(8)} ${n(probes[i].waterY)}`);
    }
    lines.push(`  ${'mean'.padEnd(8)} ${n(meanWater)}`);

    lines.push('TARGET vs ACTUAL');
    lines.push(`  heave  t ${n(frame.target.heave)}  a ${n(frame.actual.heave)}`);
    lines.push(`  pitch  t ${n(frame.target.pitch)}  a ${n(frame.actual.pitch)}`);
    lines.push(`  roll   t ${n(frame.target.roll)}  a ${n(frame.actual.roll)}`);

    lines.push('VECTORS');
    lines.push(`  wind   ${n(windStrength)}`);
    lines.push(`  thrust ${n(frame.vectors.thrust)}`);
    lines.push(`  rudder ${n(frame.vectors.rudder)}`);
    lines.push(`  vel    ${n(velocitySpeed)}`);

    return lines.join('\n');
  }

  private createReadout(): { root: HTMLElement; body: HTMLElement } {
    const root = document.createElement('div');
    root.id = 'sea-viz-readout';
    root.dataset.seaVizReadout = '';
    Object.assign(root.style, {
      position: 'fixed',
      right: 'max(14px, env(safe-area-inset-right))',
      bottom: '92px',
      zIndex: '60',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      maxWidth: 'min(260px, 92vw)',
      padding: '8px 10px',
      border: '1px solid rgba(201, 161, 90, 0.55)',
      borderRadius: '3px',
      background:
        'repeating-linear-gradient(90deg, rgba(0, 0, 0, 0.18) 0 2px, rgba(0, 0, 0, 0) 2px 6px), ' +
        'linear-gradient(160deg, rgba(44, 33, 24, 0.94) 0%, rgba(23, 16, 8, 0.96) 100%)',
      boxShadow:
        '0 8px 24px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(231, 216, 176, 0.16), ' +
        'inset 0 0 0 1px rgba(0, 0, 0, 0.35)',
      backdropFilter: 'blur(6px)',
      color: '#e7d8b0',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      fontSize: '0.66rem',
      lineHeight: '1.4',
      letterSpacing: 'normal',
      textTransform: 'none',
      pointerEvents: 'none',
    });

    const title = document.createElement('div');
    title.textContent = 'SeaViz';
    Object.assign(title.style, {
      color: '#e7d8b0',
      letterSpacing: '0.14em',
      opacity: '0.7',
    });

    const body = document.createElement('pre');
    body.style.margin = '0';
    body.style.whiteSpace = 'pre';
    body.style.fontVariantNumeric = 'tabular-nums';
    body.style.color = '#e7d8b0';
    body.style.textShadow = '0 1px 0 rgba(0, 0, 0, 0.6)';

    root.appendChild(title);
    root.appendChild(body);
    document.body.appendChild(root);
    return { root, body };
  }
}
